import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./HowToBuy.css";
import leftVid from "./e1.png";
import rightVid from "./e2.png";
import { useLanguage } from "../../context/LanguageContext";
import { translations, getT } from "../../translations";

gsap.registerPlugin(ScrollTrigger);

// Small SVG flower for falling animation (milk dragon theme colors)
const FlowerIcon = ({ size = 24, hue = "cream" }) => {
  const colors = {
    cream: "#fef3c7",
    blossom: "#fbcfe8",
    mint: "#a7f3d0",
    sky: "#bae6fd",
    lavender: "#e9d5ff",
  };
  const fill = colors[hue] || colors.cream;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className="falling-flower-icon">
      <circle cx="12" cy="12" r="2" fill="#fef9c3" />
      <ellipse cx="12" cy="7" rx="2.5" ry="4" fill={fill} opacity={0.95} />
      <ellipse cx="17" cy="12" rx="2.5" ry="4" fill={fill} opacity={0.95} transform="rotate(72 12 12)" />
      <ellipse cx="12" cy="17" rx="2.5" ry="4" fill={fill} opacity={0.95} transform="rotate(144 12 12)" />
      <ellipse cx="7" cy="12" rx="2.5" ry="4" fill={fill} opacity={0.95} transform="rotate(216 12 12)" />
      <ellipse cx="14.5" cy="9" rx="2.5" ry="4" fill={fill} opacity={0.95} transform="rotate(288 12 12)" />
    </svg>
  );
};

const FLOWER_HUES = ["cream", "blossom", "mint", "sky", "lavender"];
const FLOWER_COUNT = 18;

function HowToBuy() {
  const { lang } = useLanguage();
  const t = getT(lang);
  const faqs = translations[lang]?.howToBuy?.faqs ?? translations.zh.howToBuy.faqs;
  const [openIndex, setOpenIndex] = useState(null);
  const flowersRef = useRef([]);
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const faqContainerRef = useRef(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  useEffect(() => {
    const flowers = flowersRef.current.filter(Boolean);
    if (!flowers.length || !sectionRef.current) return;

    const tweens = [];
    flowers.forEach((el) => {
      const startX = Math.random() * 100;
      const duration = 10 + Math.random() * 12;
      const delay = Math.random() * 8;
      const rotation = (Math.random() - 0.5) * 200;
      gsap.set(el, { xPercent: -50, left: `${startX}%`, top: "-8%", rotation: 0 });
      const tween = gsap.to(el, {
        top: "108%",
        rotation,
        duration,
        delay,
        repeat: -1,
        ease: "none",
        onRepeat: () => {
          gsap.set(el, { top: "-8%", left: `${Math.random() * 100}%`, rotation: 0 });
        },
      });
      tweens.push(tween);
    });
    return () => tweens.forEach((t) => t.kill());
  }, []);

  useEffect(() => {
    const title = titleRef.current;
    const container = faqContainerRef.current;
    const section = sectionRef.current;
    if (!title || !container || !section) return;

    gsap.set([title, container], { opacity: 0, y: 40 });
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top 70%",
        toggleActions: "play none none none",
      },
    });
    tl.to(title, { opacity: 1, y: 0, duration: 0.8, ease: "power2.out" })
      .to(container, { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" }, "-=0.4");
    return () => tl.scrollTrigger?.kill();
  }, []);

  return (
    <section id="how-to-buy" className="howtobuy" ref={sectionRef}>
      {/* Falling flowers layer */}
      <div className="howtobuy-flowers" aria-hidden="true">
        {Array.from({ length: FLOWER_COUNT }, (_, i) => (
          <div
            key={i}
            className="falling-flower"
            ref={(el) => (flowersRef.current[i] = el)}
            style={{ left: `${(i * 100) / FLOWER_COUNT}%` }}
          >
            <FlowerIcon size={20 + (i % 5) * 6} hue={FLOWER_HUES[i % FLOWER_HUES.length]} />
          </div>
        ))}
      </div>

      {/* Left & right images (desktop only) */}
      <img
        src={leftVid}
        alt=""
        className="side-video left desktop-only"
      />
      <img
        src={rightVid}
        alt=""
        className="side-video right desktop-only"
      />

      {/* FAQ section */}
      <div className="faq-container" ref={faqContainerRef}>
        <h2 className="faq-title" ref={titleRef}>{t("howToBuy.title")}</h2>

        {faqs.map((faq, index) => (
          <div
            key={index}
            className={`faq-item ${openIndex === index ? "open" : ""}`}
            onClick={() => toggleFAQ(index)}
          >
            <div className="faq-header">
              <h3 className="centerith3f">{faq.title}</h3>
              <span className="faq-icon">{openIndex === index ? "−" : "+"}</span>
            </div>

            <AnimatePresence>
              {openIndex === index && (
                <motion.div
                  className="faq-content"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <p>{faq.content}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}

        {/* Mobile image */}
        <img
          src={rightVid}
          alt=""
          className="mobile-video"
        />
      </div>
    </section>
  );
}

export default HowToBuy;
